import {
  BottomSheetBackdrop,
  type BottomSheetModal,
} from '@gorhom/bottom-sheet'
import {
  type ComponentProps,
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

export interface NestedSheet<TScreen extends string> {
  nestedSheetRef: RefObject<BottomSheetModal | null>
  nestedScreen: TScreen | null
  setNestedScreen: (screen: TScreen | null) => void
  openNestedScreen: (screen: TScreen) => void
  closeNestedScreen: () => void
  renderNestedBackdrop: (
    props: ComponentProps<typeof BottomSheetBackdrop>,
  ) => ReactNode
}

/**
 * Which overlay screen is showing, plus the sheet handle and backdrop the
 * caller feeds to its BottomSheetModal. `TScreen` is the flow's own union of
 * overlay screen names, so each flow keeps its screens typed to its own set.
 */
export function useNestedSheet<TScreen extends string>(): NestedSheet<TScreen> {
  const nestedSheetRef = useRef<BottomSheetModal>(null)
  const [nestedScreen, setNestedScreen] = useState<TScreen | null>(null)
  const openNestedScreen = useCallback(
    (screen: TScreen) => {
      // Setting the same name again is no state change, so the effect below
      // never runs and a press during the closing spring would be swallowed.
      if (screen === nestedScreen) {
        nestedSheetRef.current?.present()
        return
      }
      setNestedScreen(screen)
    },
    [nestedScreen],
  )
  useEffect(() => {
    if (nestedScreen) {
      nestedSheetRef.current?.present()
    }
  }, [nestedScreen])
  // `close` rather than `dismiss`: gorhom force-closes on the latter, and a
  // forced close cannot be interrupted by a press that reopens the sheet.
  const closeNestedScreen = useCallback(() => {
    nestedSheetRef.current?.close()
  }, [])
  const renderNestedBackdrop = useCallback(
    (props: ComponentProps<typeof BottomSheetBackdrop>) => (
      <BottomSheetBackdrop
        {...props}
        appearsOnIndex={0}
        disappearsOnIndex={-1}
        pressBehavior="close"
      />
    ),
    [],
  )
  return {
    nestedSheetRef,
    nestedScreen,
    setNestedScreen,
    openNestedScreen,
    closeNestedScreen,
    renderNestedBackdrop,
  }
}
